// Admin — INBOUND · Suggestions à trier.
//
// Unified page for the categorised suggestion queue. Replaces the old
// producer-only leads page. Admin can filter by category, bulk-move
// mis-classified items, bulk-dismiss and manually create a suggestion.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Inbox, Loader2, RefreshCw, Trash2, MoveRight, Plus, Check } from "lucide-react";
import { api, CATEGORIES } from "@/pages/admin/inboundApi";

const AdminInboundSuggestionsPage = () => {
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("new");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [moveTo, setMoveTo] = useState("producer");
  const [newQuery, setNewQuery] = useState("");
  const [newCategory, setNewCategory] = useState("producer");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api(`/api/admin/inbound/suggestions?category=${category}&status=${status}&limit=200`);
      setItems(r.items || []);
      setSelected(new Set());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [category, status]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => {
    const n = new Set(selected);
    if (n.has(key)) n.delete(key); else n.add(key);
    setSelected(n);
  };
  const toggleAll = () => setSelected(selected.size === items.length ? new Set() : new Set(items.map((i) => i.key)));

  const bulkMove = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Déplacer ${selected.size} suggestion(s) vers "${moveTo}" ?`)) return;
    try {
      await api("/api/admin/inbound/suggestions/bulk-move", { method: "POST", body: JSON.stringify({ keys: Array.from(selected), to_category: moveTo }) });
      load();
    } catch (e) { setError(e.message); }
  };

  const bulkStatus = async (newStatus) => {
    if (selected.size === 0) return;
    try {
      await api("/api/admin/inbound/suggestions/bulk-status", { method: "POST", body: JSON.stringify({ keys: Array.from(selected), status: newStatus }) });
      load();
    } catch (e) { setError(e.message); }
  };

  const create = async () => {
    if (!newQuery.trim()) return;
    try {
      await api("/api/admin/inbound/suggestions", { method: "POST", body: JSON.stringify({ query: newQuery.trim(), category: newCategory }) });
      setNewQuery("");
      load();
    } catch (e) { setError(e.message); }
  };

  const counts = useMemo(() => {
    const c = { producer: 0, appellation: 0, cepage: 0, article: 0 };
    items.forEach((it) => { const cat = it.last_kind_intent || "producer"; if (c[cat] != null) c[cat] += 1; });
    return c;
  }, [items]);

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="admin-inbound-suggestions">
      <div className="flex items-center gap-3 mb-6">
        <Inbox size={26} className="text-[color:var(--gv-pink)]" />
        <h1 className="text-3xl font-serif">Suggestions à trier</h1>
      </div>

      <p className="text-[13.5px] text-[color:var(--gv-ink)]/60 mb-6 max-w-2xl">
        Les visiteurs suggèrent des fiches manquantes via la barre de recherche. Chaque suggestion porte
        maintenant une catégorie (Producer / Appellation / Cépage / Article). Trie-les ou déplace-les si
        elles ont été mal classées.
      </p>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[5px] text-[13px] mb-4">{error}</div>}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-[13.5px]" data-testid="filter-category">
          <option value="all">Toutes catégories</option>
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-[13.5px]" data-testid="filter-status">
          <option value="new">Nouvelles</option>
          <option value="actioned">Traitées</option>
          <option value="dismissed">Rejetées</option>
        </select>
        <button onClick={load} className="text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-pink)]" data-testid="refresh"><RefreshCw size={14} /></button>
      </div>

      {/* Bulk actions */}
      <div className="bg-[color:var(--gv-mist)] border border-[color:var(--gv-line)] rounded-[5px] p-3 mb-4 flex flex-wrap items-center gap-3 text-[13px]" data-testid="bulk-actions">
        <span className="text-[color:var(--gv-ink)]/70">{selected.size} sélectionnée(s)</span>
        <div className="flex items-center gap-2">
          <span className="text-[color:var(--gv-ink)]/55">Déplacer vers</span>
          <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)} className="border border-[color:var(--gv-line)] rounded-[4px] px-2 py-1 bg-white" data-testid="move-target">
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <button onClick={bulkMove} disabled={selected.size === 0} className="inline-flex items-center gap-1 bg-[color:var(--gv-ink)] text-white px-3 py-1.5 rounded-[4px] text-[12px] disabled:opacity-40" data-testid="bulk-move-btn">
            <MoveRight size={12} /> Déplacer
          </button>
        </div>
        <button onClick={() => bulkStatus("actioned")} disabled={selected.size === 0} className="inline-flex items-center gap-1 bg-emerald-600 text-white px-3 py-1.5 rounded-[4px] text-[12px] disabled:opacity-40" data-testid="bulk-actioned-btn">
          <Check size={12} /> Marquer traitées
        </button>
        <button onClick={() => bulkStatus("dismissed")} disabled={selected.size === 0} className="inline-flex items-center gap-1 bg-red-500 text-white px-3 py-1.5 rounded-[4px] text-[12px] disabled:opacity-40" data-testid="bulk-dismissed-btn">
          <Trash2 size={12} /> Rejeter
        </button>
      </div>

      {/* Create suggestion manually */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-3 mb-4 flex flex-wrap items-center gap-2 text-[13px]" data-testid="create-suggestion">
        <Plus size={14} className="text-[color:var(--gv-pink)]" />
        <input type="text" value={newQuery} onChange={(e) => setNewQuery(e.target.value)} placeholder="Créer une suggestion manuellement" className="flex-1 border border-[color:var(--gv-line)] rounded-[4px] px-2 py-1 min-w-[240px]" data-testid="new-query" />
        <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)} className="border border-[color:var(--gv-line)] rounded-[4px] px-2 py-1" data-testid="new-category">
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <button onClick={create} disabled={!newQuery.trim()} className="bg-[color:var(--gv-pink)] text-white px-3 py-1 rounded-[4px] disabled:opacity-40" data-testid="new-submit">Ajouter</button>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-8 text-center"><Loader2 size={20} className="animate-spin inline text-[color:var(--gv-pink)]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-[color:var(--gv-line)] rounded-[5px] py-12 text-center text-[color:var(--gv-ink)]/55" data-testid="empty">
          Aucune suggestion pour ces filtres.
        </div>
      ) : (
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden">
          <table className="w-full text-[13.5px]">
            <thead className="bg-[color:var(--gv-mist)] text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55">
              <tr>
                <th className="p-2 w-8"><input type="checkbox" checked={selected.size === items.length && items.length > 0} onChange={toggleAll} className="accent-[color:var(--gv-pink)]" data-testid="toggle-all" /></th>
                <th className="p-2 text-left">Requête</th>
                <th className="p-2 text-left">Catégorie</th>
                <th className="p-2 text-left">Signal</th>
                <th className="p-2 text-left">Vu</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.key} className="border-t border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)]/30" data-testid={`row-${it.key}`}>
                  <td className="p-2"><input type="checkbox" checked={selected.has(it.key)} onChange={() => toggle(it.key)} className="accent-[color:var(--gv-pink)]" /></td>
                  <td className="p-2 font-medium">{it.first_query || it.last_query}</td>
                  <td className="p-2"><span className="inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest bg-[color:var(--gv-pink)]/10 text-[color:var(--gv-pink)]">{it.last_kind_intent || "producer"}</span></td>
                  <td className="p-2 text-[color:var(--gv-ink)]/70">×{it.count} · {(it.langs || []).join("/")}</td>
                  <td className="p-2 text-[color:var(--gv-ink)]/55 text-[11.5px]">{it.last_seen_at?.slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminInboundSuggestionsPage;
